/**
 * What Stage 1 and its Job cockpit say about themselves, said once (Lucid 012, SPEC-2026-10-20-stage1-workbench).
 *
 * Before 2026-10-20 the same state was worded in up to five places at once: the Stage 1 banner, the Sorting Floor, the footer bar, the
 * AI pill and NEXT. The banner and footer words disagreed (the banner said "Ready to Review" when a summary existed, the footer said
 * "Ready to review project details" for any data). These pure functions are the one place each sentence comes from:
 *
 *   stage1Status      the one chip in the Stage 1 header (the flow states the cockpit line does not say)
 *   filesPaneSummary  the Files pane head: reading progress, outcome counts, and the rows that need a word
 *   filesTabModel     the Job cockpit's Files tab while files build in: the count, the placeholders, the files held back
 *   cockpitStateLine  the Job cockpit's one state line ("Reading 7 of 10 files", "10 files read · Summary ready")
 *   documentKindLabel what a document is (RFQ, Plan, Report, Notes, Estimator notes, Competitor, Past quote, Correspondence,
 *                     Other), shown beside its type in the Files pane
 *   clarifyItems      the Summary tab's "to clarify" list, which the header count is the length of (the old count added a second list)
 */
import { outcomeCaption, summariseFloor } from './stage1-import-outcome';
import { floorRowVideo, resolveSortingFloorDocumentLabel, type ProcessingRunSummary } from '../pages/stages/stage1/processingReducer';
import { documentSourceIdentity } from '../utils/merge-docs-by-identity';
import { isOperatorDocumentClassSource, resolveDocumentClassification } from '../utils/document-classification';
import { videoFramesPhrase, type VideoFrameCount } from '../utils/video-frames';

export type StatusTone = 'brand' | 'success' | 'warning' | 'danger' | 'neutral';

/** The wait before Full AI tries a failed summary once more (SPEC 2026-10-21 H2). */
export const SUMMARY_FAILURE_RETRY_MS = 3000;

/** Stage 1's header while it waits to try a failed summary again. */
export const SUMMARY_FAILURE_RETRY_NOTICE = 'Summary failed, trying again';

export interface Stage1StatusInput {
    procState?: { totalFiles?: number; processedFiles?: number } | null;
    /** Files are being post-processed, details extracted or the RFQ read (not the per-file reading). */
    isProcessing?: boolean;
    isSummarizing?: boolean;
    projectSummary?: unknown;
    summaryError?: string | null;
    /** Full AI waits to try the failed summary once more by itself (App, SPEC 2026-10-21 H2). */
    summaryRetrying?: boolean;
    /** The Summary on screen is out of date and QuoteMaker will not remake it by itself (use-project-summary.ts success latch). */
    summaryRerunHeld?: boolean;
    /** The Summary on screen no longer matches the job (summaryNeedsRefresh): after Risk has run, on Fast-Track, while it waits. */
    summaryNeedsRefresh?: boolean;
    /** There is something to summarise (the Summary's own admission rule). */
    canGenerate?: boolean;
    /**
     * The Job cockpit's state line is on screen: the cockpit is open, or this is a phone, whose shut rail shows the line
     * too. Then the line says the files and the summary state, and the chip leaves them out (SPEC 2026-10-21 H1). Only a
     * desktop with the cockpit shut, whose rail has no words, needs the chip to say them.
     */
    cockpitLineShown?: boolean;
}

/**
 * The one Stage 1 status chip. Busy states first, then a failure, then an out-of-date Summary, then what the operator can do next.
 * Each state is said once on screen (SPEC 2026-10-21 H1). While the Job cockpit's state line shows ("Reading 7 of 10 files",
 * "10 files read · Summary ready"), the chip leaves out what the line says (reading the files, writing the summary, ready, out
 * of date, failed) and is null when nothing else applies. A desktop with the cockpit shut shows no line, so there the chip
 * says them, in the line's words.
 */
export function stage1Status(input: Stage1StatusInput): { label: string; tone: StatusTone } | null {
    const lineSays = input.cockpitLineShown === true;
    const total = Number(input.procState?.totalFiles || 0);
    const processed = Number(input.procState?.processedFiles || 0);
    if (total > 0 && processed < total) return lineSays ? null : { label: 'Reading files', tone: 'brand' };
    if (input.isProcessing) return { label: 'Processing files', tone: 'brand' };
    if (input.isSummarizing && !lineSays) return { label: 'Writing the summary', tone: 'brand' };
    if (input.isSummarizing) return null;
    if (input.summaryError) {
        if (lineSays) return null;
        return { label: input.summaryRetrying ? SUMMARY_FAILURE_RETRY_NOTICE : 'Summary failed', tone: 'danger' };
    }
    if (input.projectSummary && (input.summaryRerunHeld || input.summaryNeedsRefresh)) {
        return lineSays ? null : { label: 'Summary out of date', tone: 'warning' };
    }
    if (input.projectSummary) return lineSays ? null : { label: 'Summary ready', tone: 'success' };
    if (input.canGenerate === false) return { label: 'Add notes or a file', tone: 'neutral' };
    return { label: 'Ready for summary', tone: 'success' };
}

export interface FilesPaneRow {
    id: string;
    name: string;
    /** queued | processing for a file still being read; the outcome for a settled one. */
    state: 'queued' | 'processing' | 'partial' | 'reference_only' | 'failed' | 'cancelled';
    caption: string;
    detail: string;
    /** A file in flight: the bytes the builder added, so its placeholder can be opened before it is read. */
    sourceFile?: Blob | null;
}

export interface FilesPaneSummary {
    total: number;
    processed: number;
    reading: boolean;
    /** Files still queued or being read. */
    inFlight: FilesPaneRow[];
    /** Settled files that did not end as a plain success. */
    problems: FilesPaneRow[];
    chips: Array<{ label: string; tone: 'attn' | 'danger' | 'neutral' }>;
}

type LooseRow = Record<string, any>;

function kindOf(row: LooseRow): 'document' | 'video' | 'other' {
    if (row.category === 'video') return 'video';
    if (row.category === 'document' || row.category === 'text' || row.category === 'email') return 'document';
    return 'other';
}

/** The Files pane head and its rows, from the import's per-file rows (procState.files). */
export function filesPaneSummary(procState: { files?: unknown[]; totalFiles?: number; processedFiles?: number } | null | undefined): FilesPaneSummary {
    const files = (Array.isArray(procState?.files) ? procState!.files : []) as LooseRow[];
    const total = Number(procState?.totalFiles || files.length || 0);
    const processed = Number(procState?.processedFiles || 0);
    const reading = total > 0 && processed < total;
    const inFlight: FilesPaneRow[] = files
        .filter(file => file?.status === 'queued' || file?.status === 'processing')
        .map(file => ({
            id: String(file.id || file.name),
            name: String(file.name || 'File'),
            state: file.status,
            caption: file.status === 'processing' ? 'Reading' : 'Waiting',
            detail: '',
        }));
    const problems: FilesPaneRow[] = files
        .filter(file => (file?.status === 'done' || file?.status === 'error') && outcomeCaption(file.outcome, kindOf(file)))
        .map(file => ({
            id: String(file.id || file.name),
            name: String(file.name || 'File'),
            state: file.outcome,
            caption: outcomeCaption(file.outcome, kindOf(file)),
            detail: typeof file.message === 'string' ? file.message : '',
        }));
    // An error row that predates outcomes (no outcome field) is still a file that was not read.
    files
        .filter(file => file?.status === 'error' && !outcomeCaption(file.outcome, kindOf(file)))
        .forEach(file => problems.push({
            id: String(file.id || file.name),
            name: String(file.name || 'File'),
            state: 'failed',
            caption: outcomeCaption('failed', kindOf(file)),
            detail: typeof file.message === 'string' ? file.message : '',
        }));
    const floor = summariseFloor(files);
    const chips: FilesPaneSummary['chips'] = [];
    if (floor.failed > 0) chips.push({ label: `${floor.failed} not read`, tone: 'danger' });
    if (floor.partial > 0) chips.push({ label: `${floor.partial} partly read`, tone: 'attn' });
    if (floor.referenceOnly > 0) chips.push({ label: `${floor.referenceOnly} reference only`, tone: 'neutral' });
    if (floor.cancelled > 0) chips.push({ label: `${floor.cancelled} cancelled`, tone: 'neutral' });
    return { total, processed, reading, inFlight, problems, chips };
}

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;
const needsALook = (count: number) => `${count} need${count === 1 ? 's' : ''} a look`;

export interface FilesTabModel {
    /** reading: this import has files still in flight. settled: it ended in this session. idle: no import here (a loaded job). */
    phase: 'reading' | 'settled' | 'idle';
    /** This import's files, settled and in all; a video's transcript row is not a file of its own. */
    total: number;
    processed: number;
    /** The files still in flight, drawn as quiet placeholders until they join the list. */
    placeholders: FilesPaneRow[];
    waiting: number;
    lastRun: ProcessingRunSummary | null;
    /** The documents and videos to list: the ones a row is still reading are held back until it settles. */
    documents: any[];
    videos: any[];
    /** The photos to list: a frame of a clip still being read is held back with its clip (verify G4). */
    photos: any[];
}

/** The listed document a row is reading: its physical source, else the one document of that name (findStage1DocumentBySource). */
function rowDocument(documents: any[], row: LooseRow): any | null {
    const identity = row.sourceIdentity || documentSourceIdentity(row.sourceFile);
    if (identity) return documents.find(doc => documentSourceIdentity(doc) === identity) || null;
    const named = documents.filter(doc => doc?.name === row.name);
    return named.length === 1 ? named[0] : null;
}

/**
 * The Job cockpit's Files tab (SPEC 2026-10-21 B9). Files build in: a photo joins the list as it finishes (it enters
 * photos[] then), while a document enters documents[] before its batch settles and a video enters videos[] before its clip
 * is read, so those are held back while their row is in flight (keyed on the row's status, never on `processed`). The
 * count leaves out transcript rows, which a clip adds already done, so "Reading 4 of 10" never turns into "4 of 11".
 * A clip's frames enter photos[] while the clip is still read (Stage1DataEntry buildFramePhotos), each carrying the clip's
 * id: they are held back with the clip and join with it. Pure: it moves no settle point.
 */
export function filesTabModel(
    procState: { files?: unknown[]; lastRun?: ProcessingRunSummary | null } | null | undefined,
    { documents = [], videos = [], photos = [] }: { documents?: unknown; videos?: unknown; photos?: unknown } = {},
): FilesTabModel {
    const files = (Array.isArray(procState?.files) ? procState!.files : []) as LooseRow[];
    const docs = Array.isArray(documents) ? documents : [];
    const clips = Array.isArray(videos) ? videos : [];
    const pictures = Array.isArray(photos) ? photos : [];
    const rows = files.filter(file => file?.category !== 'transcript');
    const inFlight = rows.filter(file => file?.status === 'queued' || file?.status === 'processing');
    const heldDocuments = new Set<any>();
    const heldVideos = new Set<any>();
    for (const row of inFlight) {
        if (row.category === 'video') {
            // The listed video this row is reading, which stays held back until the row settles.
            const video = floorRowVideo(clips, row);
            if (video) heldVideos.add(video);
        } else {
            const doc = rowDocument(docs, row);
            if (doc) heldDocuments.add(doc);
        }
    }
    const placeholders: FilesPaneRow[] = inFlight.map(file => {
        const caption = file.status === 'processing' ? 'Reading' : 'Waiting';
        const message = typeof file.message === 'string' ? file.message.trim() : '';
        return {
            id: String(file.id || file.name),
            name: String(file.name || 'File'),
            state: file.status,
            caption,
            // The step it is on (Transcribing audio...), not a second "Reading".
            detail: message && !message.toLowerCase().startsWith(caption.toLowerCase()) ? message : '',
            // Peter 2026-11-02: a file can be opened while it is still being read (Stage1InFlightFilePreview).
            sourceFile: typeof Blob !== 'undefined' && file.sourceFile instanceof Blob ? file.sourceFile : null,
        };
    });
    const lastRun = procState?.lastRun ?? null;
    const reading = inFlight.length > 0;
    const heldClipIds = new Set([...heldVideos].map(video => String(video?.id ?? '')).filter(Boolean));
    return {
        phase: reading ? 'reading' : lastRun ? 'settled' : 'idle',
        total: rows.length,
        processed: rows.length - inFlight.length,
        placeholders,
        waiting: inFlight.filter(file => file.status === 'queued').length,
        lastRun,
        documents: heldDocuments.size > 0 ? docs.filter(doc => !heldDocuments.has(doc)) : docs,
        videos: heldVideos.size > 0 ? clips.filter(video => !heldVideos.has(video)) : clips,
        photos: heldClipIds.size > 0 ? pictures.filter(photo => !(photo?.fromVideo && heldClipIds.has(String(photo.videoId ?? '')))) : pictures,
    };
}

const runLookCount = (run: ProcessingRunSummary) => run.failed + run.partial + run.referenceOnly;

/**
 * Whether the last import's count still describes the files on screen. It stops once the list holds fewer files than the
 * import read (a file removed, Remove all, an audio file whose words went into the notes and never joined the list), or once
 * the files that need a look are not the ones it counted (a reconcile brought back an older file that was not read).
 * `lookCount` is the rows that need a look now: filesPaneSummary's problems, the cancelled ones left out.
 */
export function lastRunStillShown(run: ProcessingRunSummary | null | undefined, listCount: number, lookCount: number): run is ProcessingRunSummary {
    if (!run || run.total === 0) return false;
    return listCount >= run.read && lookCount === runLookCount(run);
}

/** What the last import came to, said once: "10 files read", "8 of 10 read, 2 need a look", "Stopped: 6 read, 4 cancelled". */
export function lastRunLine(run: ProcessingRunSummary): string {
    const look = runLookCount(run);
    if (run.cancelled > 0) return `Stopped: ${run.read} read, ${run.cancelled} cancelled${look > 0 ? `, ${needsALook(look)}` : ''}`;
    if (look > 0) return `${run.read} of ${run.total} read, ${needsALook(look)}`;
    return `${plural(run.total, 'file')} read`;
}

export type CockpitSummaryState = 'writing' | 'ready' | 'stale' | 'failed' | 'retrying' | null;

const SUMMARY_WORDS: Record<Exclude<CockpitSummaryState, null>, string> = {
    writing: 'Writing the summary',
    ready: 'Summary ready',
    stale: 'Summary out of date',
    failed: 'Summary failed',
    retrying: SUMMARY_FAILURE_RETRY_NOTICE,
};

/** The words the state line uses for a summary state, or null for none. */
export function cockpitSummaryWords(summary: CockpitSummaryState): string | null {
    return summary ? SUMMARY_WORDS[summary] : null;
}

/**
 * The summary's part of the state line, from the same facts the Summary tab shows. While Full AI waits to try a failed
 * summary again (H2) the line says so, so the header chip does not say it a second time (H1).
 */
export function cockpitSummaryState({ isSummarizing, summaryError, projectSummary, stale, retrying = false }: {
    isSummarizing?: boolean; summaryError?: string | null; projectSummary?: unknown; stale?: boolean; retrying?: boolean;
}): CockpitSummaryState {
    if (isSummarizing) return 'writing';
    if (summaryError) return retrying ? 'retrying' : 'failed';
    if (projectSummary && stale) return 'stale';
    if (projectSummary) return 'ready';
    return null;
}

/**
 * The Job cockpit's one state line: the files first (reading, or how the import ended, or how many the job has), then the
 * summary, joined with " · ". The Stage 1 header chip does not repeat the file count (B8). The files that need a look are
 * counted once, from the rows the pane lists (`lookCount`), so the line never says it twice or disagrees with the list.
 * `listCount` is the files the customer sent; the frames taken from their videos follow them once the reading is over
 * ("22 files, 11 frames from 3 videos", Lucid 016).
 */
export function cockpitStateLine(model: FilesTabModel, listCount: number, summary: CockpitSummaryState, lookCount = 0, frames: VideoFrameCount | null = null): string {
    const framesWords = frames ? videoFramesPhrase(frames) : '';
    const listed = listCount > 0 ? plural(listCount, 'file') : framesWords ? '' : 'No files yet';
    const settled = lastRunStillShown(model.lastRun, listCount, lookCount)
        ? lastRunLine(model.lastRun)
        : lookCount > 0 ? `${listed}, ${needsALook(lookCount)}` : listed;
    const files = model.phase === 'reading'
        ? `Reading ${model.processed} of ${plural(model.total, 'file')}`
        : [settled, framesWords].filter(Boolean).join(', ');
    const words = cockpitSummaryWords(summary);
    return words ? `${files} · ${words}` : files;
}

const DOCUMENT_KIND_WORDS: Record<string, string> = {
    rfq: 'RFQ',
    plan: 'Plan',
    report: 'Report',
    assessor_notes: 'Notes',
    estimator_notes: 'Estimator notes',
};

// The classes outside the lanes. v6.0.102 showed them as the AI's raw label on every document row; the Files pane dropped them.
// A long word does not squeeze the file name: the row wraps the kind under the name when it does not fit (stage1-workbench.css).
const DOCUMENT_CLASS_WORDS: Record<string, string> = {
    competitor_quote: 'Competitor',
    our_past_quote: 'Past quote',
    correspondence: 'Correspondence',
    other: 'Other',
};

/**
 * The lane a document is in (RFQ, Plan, Report, Notes, Estimator notes, Scope of works), from its content classification
 * (never its file name). Null outside the lanes. A Job details source chip uses this word: it is one short word.
 */
export function documentLaneLabel(doc: unknown): string | null {
    const label = resolveSortingFloorDocumentLabel(doc);
    const word = label ? DOCUMENT_KIND_WORDS[String(label)] || null : null;
    if (word) return word;
    // A pre-made scope of works (stage1-premade-scope.ts sets the type from its content) primes the method and the scope.
    return (doc as { type?: unknown } | null)?.type === 'scope_of_works' ? 'Scope of works' : null;
}

/**
 * What a document is, shown beside its type in the Files pane: its lane, else the class its content was given (Competitor,
 * Past quote, Correspondence, Other). Null when it has no class yet.
 */
export function documentKindLabel(doc: unknown): string | null {
    const lane = documentLaneLabel(doc);
    if (lane) return lane;
    const resolved = resolveDocumentClassification(doc);
    const ai = doc as { aiClassification?: unknown; type?: unknown } | null;
    // "other" is also the content classifier's answer when it finds no category (document-type-classifier.ts, confidence 30),
    // which v6.0.102 never showed: it showed the AI batch's label. So "Other" is said only when the AI batch or an operator
    // chose it.
    const classWord = (classification: unknown): string | null => {
        const key = String(classification);
        if (key === 'other' && String(ai?.aiClassification ?? '').trim().toLowerCase() !== 'other'
            && !isOperatorDocumentClassSource(resolved.source)) return null;
        return DOCUMENT_CLASS_WORDS[key] || null;
    };
    // A trusted class (an operator's, or the content classifier's own) and a direct type or AI label are the document's class.
    if (resolved.basis === 'trusted-canonical-class' || resolved.basis === 'direct-evidence') {
        const word = classWord(resolved.classification);
        if (word) return word;
    }
    // The AI batch's "correspondence" is no class the shared resolver knows (it neither opens nor vetoes the RFQ gate), so it
    // is read as the AI wrote it. It outranks the import-time class an untrusted `class` field still carries.
    const aiLabel = String(ai?.aiClassification ?? ai?.type ?? '').trim().toLowerCase();
    if (aiLabel === 'correspondence') return DOCUMENT_CLASS_WORDS.correspondence!;
    // The resolver's fallback means "no class yet", which is not the class "other".
    if (resolved.basis === 'weak-canonical-class') return classWord(resolved.classification);
    return null;
}

/**
 * The Summary tab's "to clarify" list: the clarifying questions when the summary has any (the actionable form), otherwise the gap
 * statements, each once. The header count is this list's length, so the two can never disagree.
 */
export function clarifyItems(summary: { suggested_clarifying_questions?: unknown; data_quality?: { what_we_lack?: unknown }; missing_information?: unknown } | null | undefined): { items: string[]; areQuestions: boolean } {
    const list = (value: unknown) => (Array.isArray(value) ? value : []).map(item => String(item ?? '').trim()).filter(Boolean);
    const questions = list(summary?.suggested_clarifying_questions);
    if (questions.length > 0) return { items: questions, areQuestions: true };
    const gaps = [...list(summary?.data_quality?.what_we_lack), ...list(summary?.missing_information)];
    return { items: Array.from(new Set(gaps)), areQuestions: false };
}

/** A summary's own generation stamp, from either key spelling. */
function summaryGeneratedAt(summary: unknown): string {
    const value = summary as { generated_at?: unknown; generatedAt?: unknown } | null;
    return String(value?.generated_at ?? value?.generatedAt ?? '');
}

/**
 * Whether the summary on screen landed in this session for this project (S17, B10): a run saved it after the project opened
 * (the landing count moved past the count at the open) and it is the summary now shown. A project open, a reload, a session
 * restore and a return to Stage 1 bring a summary with no landing, so the Stage 1 ready pulse does not fire for them.
 */
export function stage1SummaryLandedThisSession({ landingSeq, seqAtOpen, landingSummary, projectSummary }: {
    landingSeq: number; seqAtOpen: number; landingSummary: unknown; projectSummary: unknown;
}): boolean {
    if (!projectSummary || !landingSummary || !(landingSeq > seqAtOpen)) return false;
    const stamp = summaryGeneratedAt(landingSummary);
    return landingSummary === projectSummary || (!!stamp && stamp === summaryGeneratedAt(projectSummary));
}

export interface Stage1HeaderStatus {
    label: string;
    tone: string;
    /** The chip as a button (the "N to clarify" jump, S8). */
    onClick?: (() => void) | null;
    title?: string;
}

/**
 * The Stage 1 header status: while the summary runs or files are read, the
 * ordinary Stage 1 status; then App's notice (K11: why Stage 1 waits); then a
 * ready summary's "N to clarify", which jumps to the list (S8); otherwise the
 * ordinary Stage 1 status. The Risk countdown and its Hold that used to sit on
 * top went with the old pipeline (docs/old-pipeline-removal/PLAN.md).
 */
export function stage1HeaderStatus({
    base,
    hasSummary,
    busy,
    filesBusy = false,
    notice = null,
    clarify = null,
}: {
    /** The ordinary Stage 1 status (stage1Status), or null when the cockpit line says it all. */
    base: Stage1HeaderStatus | null;
    hasSummary: boolean;
    /** The summary runs: its own status wins. */
    busy: boolean;
    /** Files are being read or processed: their status wins over the notice and the clarify count. */
    filesBusy?: boolean;
    notice?: string | null;
    /** A ready, up-to-date summary's clarify count and its jump. */
    clarify?: { count: number; onClick: () => void } | null;
}): Stage1HeaderStatus | null {
    if (busy || filesBusy) return base;
    if (notice) return { label: notice, tone: 'neutral' };
    if (clarify && clarify.count > 0 && hasSummary) {
        return {
            label: `${clarify.count} to clarify`,
            tone: 'warning',
            onClick: clarify.onClick,
            title: `Show the ${clarify.count === 1 ? 'item' : `${clarify.count} items`} to clarify before quoting`,
        };
    }
    return base;
}

/**
 * Why Stage 1 is waiting, in one sentence for its header, or null (K11). Under the autonomy rule nothing between import and
 * Stage 5 waits for a press, so a wait is always one QuoteMaker ends by itself, and the header says what it waits for: an edit
 * while a summary run prepared its evidence or saved its writes stopped that run (a stale-run error), or the summary is out of
 * date and is made again about 2 s after typing stops (B2).
 * Not while the notes editor holds the AI: its own alert says so, beside the notes (A5, one place for one state). Not on
 * Fast-Track, whose summary is not remade by itself.
 */
export function stage1WaitingNotice({
    fastTrack,
    canAutoRefresh,
    hasSummary,
    summaryStale,
    runInterrupted,
    notesPaused,
    busy,
}: {
    fastTrack: boolean;
    /** The summary is still re-made by itself (no later stage complete: inspectorNotesCanAutoRefresh). */
    canAutoRefresh: boolean;
    hasSummary: boolean;
    /** The summary on screen no longer matches the job (summaryNeedsRefresh), and QuoteMaker will remake it (not held). */
    summaryStale: boolean;
    /** A summary run stopped because the job changed under it (use-project-summary onSummaryRunInterrupted). */
    runInterrupted: boolean;
    notesPaused: boolean;
    busy: boolean;
}): string | null {
    if (fastTrack || !canAutoRefresh || busy || notesPaused) return null;
    const waiting = runInterrupted || (hasSummary && summaryStale);
    return waiting ? 'Waiting: the job changed, updating the summary' : null;
}
